// The timelines' words (lane B10, slice S18; plan D12, D17; CR-093, CR-094): the tab, the fallback row and chip, the
// source tag of a row, the chip of each linked artifact, and the words every timeline kind is read with. Every entry
// quotes his glossary or the vendored design-system excerpts; the D0 label suites discover this module (a lane folder
// of src/views) and hold it to the same rules as src/mc. A word he has not confirmed is drawn pending, and only where
// the pending list carries it (Superseded, Acceptance reversal, Renege, Rescheduled).
//
// A kind whose event has no word of his is read with his nearest status word (the list is in the S18 report, for his
// review): the words here are his; which word reads which kind is ours (sentences.ts).

import { defineLabels, type LabelDef, type LabelSource } from "@/mc/labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const satisfies LabelDef;
const pending = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "pending" }) as const satisfies LabelDef;

// ---- the quotes ----------------------------------------------------------------------------------------------------

const STATIONS = "Request · Approval · Posting · Intake · Panel · Offer · Filled; Hold · Cancel · Reopen · Archive; Offer Accepted · Ready for HRIS · HRIS Hired · Filled · Archived";
const LINKED = "\"Show actor, date/time, source and linked artifacts subject to permission.\"";
const AUDIT_KEYS = "*Old → New · Actor · Timestamp · Reason · Related req / candidate · Evidence · Source*";
const MASTER_FIELDS =
  "\"Candidate ID, name, primary email, secondary email, phone, LinkedIn URL, resume versions, source, internal/external flag, talent-community status, consent/communication fields, and candidate history.\"";
const REQUEST_CHAIN = "Chain \"Draft -> Pending HM Certification -> HM Certified -> Pending HRBP Approval -> Approved.\"";
/** B2's approval fan-out (DS #39, DC-64), quoted as mc/g5/labels.ts quotes it for JOURNEY_LABELS. */
const PARALLEL = "Approval fans out into three parallel tiles (`journey.parallel`): Coordinator creates/posts · TA Manager assigns Recruiter · Compensation starts in parallel.";
const RETURNED = "\"Returned requests go back to Draft or Dormant without deleting history.\"";
const SUBMIT = "| Submit (slate / scorecard / request) | \"Recruiter submits qualified slate\"; \"submit scorecard\"; panel change \"Submitted\".";
const REQ_CHAIN = "Chain \"Request Approved -> Req Creation Pending -> Created -> Posted.\"";
const ASSIGN_CHAIN = "chain \"Recommended -> Pending TA Manager -> Assigned.\"";
const COMP_CHAIN = "Chain \"Compensation: Pending -> In Review -> Finalized.\"";
const REQ_STATES = "\"Additional states: On Hold, Cancellation Pending/Cancelled, Reopen Pending/Reopened.\"";
const CLOSE_CHAIN = "\"Statuses / workflow: Offer Accepted -> Ready for HRIS -> HRIS Hired -> Filled -> Archived.\"";
const SCREEN_CHAIN = "\"Statuses / workflow: Screen Requested -> Scheduled -> Completed -> Advance/Hold/Pass/Withdrawn.\"";
const APPLICATION_CHAIN = "\"application Sourced -> Qualified or Dispositioned\"";
const HM_REVIEW_CHAIN = "\"Statuses / workflow: Pending Review -> Advanced/Held/Passed. Held remains active with age and reason.\"";
const SELECTION_CHAIN = "\"Statuses / workflow: Pending HM Selection -> Advance/Hold/Pass. Advanced -> HM Interview Scheduling Requested.\"";
const DECISION_CHAIN = "\"Candidate decision Pending -> Advance/Hold/Pass.\"";
const OFFER_CHAIN = "Chain \"Approved -> Verbal Extended -> Written Sent -> Awaiting Candidate -> Negotiation -> Revised Offer -> Accepted/Declined.\"";
const INTAKE_CHAIN = "\"Statuses / workflow: Intake Scheduled -> In Progress -> Draft Plan -> Validation Failed or Ready -> Finalized.\"";
const JOURNEYS = "Load framework · Customize · Validate · Finalize";
const ASSESSMENT_CHAIN = "\"Statuses / workflow: Not Required or Required -> Draft -> Sent -> Submitted -> Reviewed -> Completed/Withdrawn.\"";
const SCHEDULING_CHAIN = "\"Statuses: Requested, Scheduling in Progress, Awaiting Candidate Availability, Awaiting Interviewer Availability, Scheduled, Reschedule Requested, Completed, Cancelled.\"";
const MEETINGS = "\"Meeting templates: intake, recruiter screen, HM interview, Final Interview Loop, debrief and offer call.\"";
const PACKET_CHAIN = "\"Statuses / workflow: Packet Draft -> Generated -> Reviewed -> Released.\"";
const ACK_CHAIN = "Chain \"Acknowledgment Pending -> Accepted/Declined.\"";
const SCORECARD_CHAIN = "\"Statuses / workflow: Not Started -> In Progress -> Submitted/Past Due.\"";
const DEBRIEF_CHAIN = "\"Statuses / workflow: Not Ready -> Scheduled -> Roll Call -> Reveal -> Discussion -> Alignment -> Outcome Recorded/Blocked.\"";
const REFERENCE_CHAIN = "\"Statuses / workflow: Not Required or Required -> Requested -> In Progress -> Completed/Unable to Complete.\"";
const SLATE_CHAIN = "\"Statuses / workflow: Draft Batch -> Submitted -> In HM Review -> Completed/Overdue.\"";
const PROFILE_STATUS = "\"Candidate profile Active/Archived/Merged\"";
const COMMUNITY = "\"Capture segment, tags, reason/date added, last contact, notes and prior requisition; do not auto-enroll in campaigns.\"";
const ATTESTATION = "\"Application includes self-attestation that current manager was informed; do not auto-notify manager.\"";
const MANUAL_ENTRY = "\"Required manual-entry fields are name, primary email when available, source, and owner.\"";
const REVERSAL = "Renege / acceptance reversal — V1 has it; his offer chain ends at Accepted/Declined.";
const VERSIONS = "Should the active version show *Current* and an old one *Previous version* (rather than *Superseded*)?";
const WORDS_T8 = "Words needing a glossary entry before a build uses them: *Rescheduled*, *Current*, *Previous version*, *Clear* and the Q-E15 lines.";

// ---- the screens' words ----------------------------------------------------------------------------------------------

/** The tab, the fallback row and the fallback chip. The lists' own names are D0's (HISTORY_LABELS). */
export const TIMELINE_LABEL_DEFS = {
  tab: his("Timeline"),
  timelineEvent: his("Timeline event"),
  linkedArtifact: his("Linked artifact"),
} as const satisfies Readonly<Record<string, LabelDef>>;

export const TIMELINE_LABELS = /* @__PURE__ */ defineLabels(TIMELINE_LABEL_DEFS);

/** A row's source tag, by `timeline_event.source`. `import` and `backfill` have no word of his yet: no tag. */
export const SOURCE_LABELS = /* @__PURE__ */ defineLabels({
  app: his("App"),
  system: his("System"),
  verbal: his("Verbal"),
  mail: his("Email"),
  call: his("Call"),
  paper: his("Evidence"),
});

/** A linked artifact's chip, by `ArtifactLink.type` (B10's six resolvers, and the four S14b binds after G3). */
export const ARTIFACT_LABELS = /* @__PURE__ */ defineLabels({
  resume_version: his("Resume version"),
  jd_version: his("Job Description version"),
  scorecard: his("Scorecard"),
  offer: his("Offer"),
  posting: his("Posting"),
  intake: his("Intake"),
  kickoff_email: his("Kickoff email"),
  offer_package_version: his("Offer package"),
  hris_record: his("HRIS confirmation"),
  handoff_checklist: his("Handoff checklist"),
});

// ---- the words the timeline kinds are read with ----------------------------------------------------------------------

/**
 * One definition per word. A kind reads as one word or as a lead and a word ("Compensation review · Pending"); the
 * table that maps each kind to its words is sentences.ts.
 */
export const EVENT_WORD_DEFS = {
  // the request (G §5)
  hiringRequest: his("Hiring Request"),
  draft: his("Draft"),
  submitted: his("Submitted"),
  returned: his("Returned"),
  approved: his("Approved"),
  // B2's request events, in the words B2's request History draws them (views/request/HiringRequest.tsx EVENT_WORD)
  pendingHmCertification: his("Pending HM Certification"),
  hmCertified: his("HM Certified"),
  coordinatorCreatesPosts: his("Coordinator creates/posts"),
  taManagerAssignsRecruiter: his("TA Manager assigns Recruiter"),
  compensationStartsInParallel: his("Compensation starts in parallel"),
  dormant: his("Dormant"),
  withdrawn: his("Withdrawn"),
  // the requisition's milestones (G §5, §12)
  requestApproved: his("Request Approved"),
  reqCreationPending: his("Req Creation Pending"),
  created: his("Created"),
  posted: his("Posted"),
  recruiter: his("Recruiter"),
  recommended: his("Recommended"),
  pendingTaManager: his("Pending TA Manager"),
  assigned: his("Assigned"),
  compensationReview: his("Compensation review"),
  pending: his("Pending"),
  inReview: his("In Review"),
  compFinalized: his("Finalized"),
  requestHold: his("Request hold"),
  onHold: his("On Hold"),
  cancellationPending: his("Cancellation Pending"),
  cancelled: his("Cancelled"),
  reopenPending: his("Reopen Pending"),
  reopened: his("Reopened"),
  offerAccepted: his("Offer Accepted"),
  backgroundCheck: his("Background check"),
  readyForHris: his("Ready for HRIS"),
  hrisHired: his("HRIS Hired"),
  start: his("Start"),
  filled: his("Filled"),
  archived: his("Archived"),
  // a span that ended, a record taken back (sentences.ts reads them after the word they qualify)
  resolved: his("Resolved"),
  superseded: pending("Superseded"),
  acceptanceReversal: pending("Acceptance reversal"),
  renege: pending("Renege"),
  // the checkpoints the timeline still says (G §7, §8, §11; "Onsite" reads Final Interview Loop, G §22)
  sourcing: his("Sourcing"),
  recruiterScreen: his("Recruiter screen"),
  hmInterview: his("HM interview"),
  finalInterviewLoop: his("Final Interview Loop"),
  offer: his("Offer"),
  intake: his("Intake"),
  // the application (G §7, §8, §11)
  stage: his("Stage"),
  candidateDecision: his("Candidate decision"),
  advance: his("Advance"),
  hold: his("Hold"),
  pass: his("Pass"),
  sourced: his("Sourced"),
  qualified: his("Qualified"),
  dispositioned: his("Dispositioned"),
  pendingReview: his("Pending Review"),
  advanced: his("Advanced"),
  held: his("Held"),
  passed: his("Passed"),
  pendingHmSelection: his("Pending HM Selection"),
  formalDebrief: his("Formal debrief"),
  primary: his("Primary"),
  alternate: his("Alternate"),
  alternateDesignation: his("Alternate designation"),
  additionalInterviewException: his("Additional-interview exception"),
  // the offer (G §12)
  offerPackage: his("Offer package"),
  revisedOffer: his("Revised Offer"),
  verbalExtended: his("Verbal Extended"),
  writtenSent: his("Written Sent"),
  negotiation: his("Negotiation"),
  declined: his("Declined"),
  offerDecline: his("Offer decline"),
  offerApproval: his("Offer approval"),
  counter: his("Counter"),
  offerCandidateDecision: his("Candidate decision"),
  referenceCheck: his("Reference check"),
  required: his("Required"),
  notRequired: his("Not Required"),
  inProgress: his("In Progress"),
  unableToComplete: his("Unable to Complete"),
  // the interview plan and the assessment (G §6; lane B4)
  interviewPlan: his("Interview plan"),
  loadFramework: his("Load framework"),
  customize: his("Customize"),
  validationFailed: his("Validation Failed"),
  ready: his("Ready"),
  planFinalized: his("Finalized"),
  assessment: his("Assessment"),
  sent: his("Sent"),
  reviewed: his("Reviewed"),
  // scheduling (G §9; lane B5) and its meeting templates
  schedulingRequest: his("Scheduling request"),
  requested: his("Requested"),
  scheduled: his("Scheduled"),
  rescheduled: pending("Rescheduled"),
  rescheduleRequested: his("Reschedule Requested"),
  completed: his("Completed"),
  meetingIntake: his("Intake"),
  meetingRecruiterScreen: his("Recruiter screen"),
  meetingHmInterview: his("HM interview"),
  meetingFinalInterviewLoop: his("Final Interview Loop"),
  meetingDebrief: his("Debrief"),
  meetingOfferCall: his("Offer call"),
  // packets, acknowledgment and panel change (G §10; lane B6)
  packet: his("Packet"),
  generated: his("Generated"),
  released: his("Released"),
  acknowledgment: his("Acknowledgment"),
  accepted: his("Accepted"),
  ackDeclined: his("Declined"),
  nonresponse: his("Nonresponse"),
  panelChange: his("Panel change"),
  panelChangeSubmitted: his("Submitted"),
  panelChangeBlocked: his("Blocked"),
  panelChangeApproved: his("Approved"),
  panelChangeRejected: his("Rejected"),
  panelChangeCompleted: his("Completed"),
  // scorecards, summary and debrief (G §2, §11; lane B7)
  scorecard: his("Scorecard"),
  scorecardSubmitted: his("Submitted"),
  waived: his("Waived"),
  ownScoreRevision: his("Own-score revision"),
  administrativeCorrection: his("Administrative correction"),
  concernFlag: his("Concern flag"),
  reveal: his("Reveal"),
  outcomeRecorded: his("Outcome Recorded"),
  candidateSummary: his("Candidate summary"),
  // handoff and HRIS (G §5; lane B9)
  handoffChecklist: his("Handoff checklist"),
  hrisConfirmation: his("HRIS confirmation"),
  // the candidate master, talent community and slate (G §2, §7, §8, §17; lane B3)
  candidateProfile: his("Candidate profile"),
  resumeVersion: his("Resume version"),
  humanConfirmation: his("Human confirmation"),
  reviewEdit: his("Review/edit"),
  owner: his("Owner"),
  candidateApplication: his("Candidate application"),
  potentialMatch: his("Potential match"),
  firstContact: his("First contact"),
  merged: his("Merged"),
  recover: his("Recover"),
  talentCommunity: his("Talent community"),
  addToTalentCommunity: his("Add to talent community"),
  lastContact: his("Last contact"),
  selfAttestation: his("Self-attestation"),
  candidateSlate: his("Candidate slate"),
  inHmReview: his("In HM Review"),
  overdue: his("Overdue"),
  outcome: his("Outcome"),
} as const satisfies Readonly<Record<string, LabelDef>>;

/** The texts of EVENT_WORD_DEFS, registered once so the label suites check every quote, text and status. */
export const EVENT_WORDS = /* @__PURE__ */ defineLabels(EVENT_WORD_DEFS);

export type EventWord = keyof typeof EVENT_WORD_DEFS;
